import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

import { AuthService } from '../shared/auth.service';
import {
  SessionService,
  NotificationService,
  ContainersService,
  PlatformService,
} from '../shared/services';
import { Container, User, Message, Entity } from '../shared/models';
import { AlertChangeContainerModal, ModalService } from '../shared/modules/modal';
import { EventConstants, TranslationConstants } from '../shared/constants';
import { ComponentWithErrorModal } from '../shared/components/base/component-with-error-modal';
import { ChangeContainerModal } from '../shared/components/change-container-modal/change-container-modal.component';
import { AllowedRolesForNewUI } from '../shared/models/user-permissions-policy';
import { LaunchDarklyService } from '../shared/services/launch-darkly.service';

const char2PixFactor = 9;
const char2PixAddition = 35;

@Component({
  selector: 'header-component',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent extends ComponentWithErrorModal implements OnInit, OnDestroy {
  workingContainer: Container;
  selectedContainer: Container;
  containers: Container[];
  loggedInUser: User;
  isSelectingContainer: boolean;
  is2faEnabled: boolean;
  isListeningForPlatformEvents = false;
  ContainersModule: any;
  userRole: string;
  hasNewUIAccess: boolean;
  redirectToNewUI: boolean;
  isNotIframe: boolean;
  headerText: string;
  protected subscriptions: Subscription[] = [];

  constructor(
    private _authService: AuthService,
    private _sessionService: SessionService,
    private _notificationService: NotificationService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _router: Router,
    private _containersService: ContainersService,
    private _modalService: ModalService,
    private titleService: Title,
    private _platformService: PlatformService,
    private _ngZone: NgZone,
    private _launchDarklyService: LaunchDarklyService,
    
  ) {
    super(_notificationService, _modalService);
    this.ContainersModule = this._platformService.getPlatformModule().Containers;
    this.redirectToNewUI = false;
    
    const urlParams = new URLSearchParams(window.location.search);
    this.isNotIframe = !urlParams.get('iframe') || urlParams.get('iframe') !== 'true';
  }

  ngOnInit(): void {
    this.loadTittles().then((titles)=>{
      this.headerText = titles.headerTittle;
      this.titleService.setTitle(titles.pageTittle);
    });

    this.initContainers();
    this.loggedInUser = this._sessionService.getUserDetails();
    this.isSelectingContainer = false;
    this.is2faEnabled = this._sessionService.getIs2faEnabled();
    this.listenForNotifications();
    this.userRole = this._sessionService.getUserRole();
    this.hasNewUIAccess = AllowedRolesForNewUI.includes(this.userRole);
  }

  async loadTittles(): Promise<any> {
    await this._launchDarklyService.client.waitUntilReady();
    const rebrandingEnabled = this._launchDarklyService.client.variation('tp-rebranding', false);
    const titles = { pageTittle: 'Transfer Pricing', headerTittle: 'Transfer Pricing' };
    if (rebrandingEnabled) {
      titles.pageTittle = 'Exactera';
      titles.headerTittle = 'Exactera Transfer Pricing';
    }
    return titles;
  }

  async initContainers() {
    this.containers = this._sessionService.getContainersList();
    await this.getWorkingContainer();
    this.workingContainer = this._sessionService.getWorkingContainer();
    this.selectedContainer = this.containers.find(cont => cont.containerId === this.workingContainer.containerId);
    this._changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this._launchDarklyService.client.close();
    this.subscriptions.forEach((subscription: Subscription) => { subscription.unsubscribe(); });
  }

  logout(): void {
    this._authService.logout();
  }

  private listenForNotifications(): void {
    this.subscriptions.push(
      this._notificationService.notification$.subscribe((message: Container) => {
        if (!message.containerId) {
          return;
        }
        this.workingContainer = message;
        this._changeDetectorRef.detectChanges();
      })
    );

    this.subscriptions.push(
      this._sessionService.onContainerListChangeSubscription().subscribe(() => {
        this.initContainers();
      })
    );

    if (!this.isListeningForPlatformEvents) {
      const platformModuleInstance = this._platformService.getPlatformModule();
      platformModuleInstance.Events.on(EventConstants.CONTAINER_CHANGED, async () => {
        await this.containerChangedInADifferentTab();
      });
      this.isListeningForPlatformEvents = true;
    }
  }

  async containerChangedInADifferentTab(): Promise<void> {
    const closeAll = true;
    this._modalService.closeModal(closeAll);
    const entity: Entity = await this.getWorkingContainer();
    const container = entity.container;
    const displayName = `${container.name} ${container.taxYear}`;
    this._ngZone.run(() => {
      this.alertContainerChanged(displayName);
      this.initContainers();
      this._router.navigate(['/']);
    });
  }

  containerSelected(): void {
    this._modalService.openModal({
      component: ChangeContainerModal,
      data: {
        title: TranslationConstants.GLOBAL_SWITCH_CONTAINER,
        content: TranslationConstants.GLOBAL_SWITCH_CONTAINER_CONTENT,
      } as Message,
      success: () => {
        this.containerChange();
      },
      cancel: () => {
        this.initContainers();
        // For some reason change detection is not done automatically here
        // (maybe because the modal is outside Angular's "zone" so we nee to invoke that manually)
        this._changeDetectorRef.detectChanges();
      },
    });
  }

  async containerChange(): Promise<void> {
    this.isSelectingContainer = true;
    // For some reason change detection is not done automatically here
    // (maybe because the modal is outside Angular's "zone" so we nee to invoke that manually)
    this._changeDetectorRef.detectChanges();
    this._sessionService.setWorkingContainer(this.selectedContainer);
    await this.ContainersModule.switchContainers(this.selectedContainer);
    await this.getWorkingContainer();
    this._modalService.closeModal();
    this._router.navigate(['/']);
  }

  getWorkingContainer(): Promise<Entity> {
    return new Promise((resolve) => {
      this.subscriptions.push(this._containersService.getWorkingContainerEntity()
        .subscribe(({ data }) => {
          const upe: Entity = data;
          const upeId = upe.entityId || 0;
          const upeCurrency = upe.upeCurrency || null;
          const currencyScale = upe.upeCurrencyScale || 0;
          const workingContainer = upe.container || this._sessionService.getWorkingContainer();
          this._sessionService.setContainerUpeId(upeId);
          this._sessionService.setContainerUpeCurrency(upeCurrency);
          this._sessionService.setContainerUpeCurrencyScale(currencyScale);
          this._sessionService.setWorkingContainer(workingContainer);
          this.isSelectingContainer = false;
          resolve(data);
        })
      );
    });

  }

  async alertContainerChanged(containerName) {
    this._modalService.openModal({
      component: AlertChangeContainerModal,
      data: {
        title: TranslationConstants.GLOBAL_ALERT_SWITCH_CONTAINER,
        content: TranslationConstants.GLOBAL_ALERT_SWITCH_CONTAINER_CONTENT,
        text: containerName
      } as Message,
      success: () => {
        this._modalService.closeModal();
        this._changeDetectorRef.detectChanges();
      }
    });
  }

  getWidth(): String {
    // We need the selector to be as wide as the largest container name (with the tax year),
    // not just the one that is selected
    const maxNameLength = this.containers ? Math.max(...this.containers.map(cont => cont.name.length)) + 5 : 60;
    return (maxNameLength * char2PixFactor + char2PixAddition) + 'px';
  }

  goToAdminPage(): void {
    this._router.navigate(['admin']);
  }

  enable2fa(): void {
    this._router.navigate(['setup-2fa']);
  }

  changePassword(): void {
    this._router.navigate(['change-password']);
  }

  disable2fa(): void {
    this._authService.disable2fa();
  }
}
